import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { AppFrame } from '../components/AppNav';
import { Banner, Field, Loading, Meter, Modal } from '../components/ui';
import { formatDate, formatDateTime, formatExact, timeZoneName } from '../lib/format';
import { useSession } from '../state/session';
import { useMfaGuard } from '../components/MfaGuard';
import { useToast } from '../state/toast';

export function Billing() {
  const { orgId } = useSession();
  const { notify } = useToast();
  const { guard, challenge } = useMfaGuard();
  const [data, setData] = useState<any>(null);
  const [pricing, setPricing] = useState<any>(null);
  // No card state. The card is typed on the provider's page, inside a frame
  // this code cannot read into, and all that comes back is a reference.
  const [setup, setSetup] = useState<any>(null);
  const [collecting, setCollecting] = useState(false);
  const [frameHeight, setFrameHeight] = useState(0);
  const [packs, setPacks] = useState(1);
  const [tier, setTier] = useState<'subscription' | 'credits'>('subscription');
  const [plan, setPlan] = useState('standard');
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const load = useCallback(async () => {
    if (!orgId) return;
    const [billing, prices] = await Promise.all([
      api.get(`/organizations/${orgId}/billing`),
      api.get('/pricing'),
    ]);
    setData(billing);
    setPricing(prices);
    if (billing.entitlements.plan_id !== 'credits') {
      setPlan(billing.entitlements.plan_id);
    }
  }, [orgId]);

  useEffect(() => { void load(); }, [load]);

  // Saving a reference, not a card. `handoff` is whatever the provider gave
  // back: a payment-method id from the framed page, or a completed setup
  // session id from a redirect.
  const saveReference = useCallback(async (handoff: Record<string, string>) => {
    setBusy(true); setError(null);
    try {
      await guard(async () => {
        await api.post(`/organizations/${orgId}/billing/payment-method`, handoff);
        notify('Payment method saved. This does not start your trial.', 'ok');
        setCollecting(false); setSetup(null);
        await load();
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : 'That card could not be saved.');
    } finally { setBusy(false); }
  }, [orgId, guard, notify, load]);

  const beginCardSetup = async () => {
    setBusy(true); setError(null);
    try {
      const response = await api.get(`/organizations/${orgId}/billing/payment-setup`);
      if (response.setup.mode === 'redirect') {
        // A provider-hosted page on the provider's own domain.
        window.location.assign(response.setup.url);
        return;
      }
      setSetup(response.setup);
      setCollecting(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : 'Could not start payment setup.');
    } finally { setBusy(false); }
  };

  // The framed provider page hands back the reference by postMessage. Only a
  // message from that exact origin is accepted — anything else is some other
  // page talking, and a forged token is the whole risk here.
  useEffect(() => {
    if (!setup?.origin) return;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== setup.origin) return;
      if (event.data?.type === 'payment_frame_height') {
        const height = Number(event.data.height);
        if (height > 0) setFrameHeight(Math.min(height, 900));
        return;
      }
      if (event.data?.type !== 'payment_method_tokenized') return;
      if (typeof event.data.token !== 'string') return;
      void saveReference({ provider_token: event.data.token });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [setup?.origin, saveReference]);

  // Returning from a redirect flow: the provider put its session id in the URL.
  useEffect(() => {
    if (!orgId) return;
    const params = new URLSearchParams(window.location.search);
    const reference = params.get('payment_setup');
    if (!reference || reference === 'cancelled') return;
    window.history.replaceState({}, '', window.location.pathname);
    void saveReference({ setup_reference: reference });
  }, [orgId, saveReference]);

  // Returning from subscription checkout. The webhook mirrors this too, and
  // whichever arrives first wins -- so a customer who closes the tab still
  // ends up subscribed, and this path is not the one the subscription depends
  // on. It exists so the page is correct immediately instead of eventually.
  useEffect(() => {
    if (!orgId) return;
    const params = new URLSearchParams(window.location.search);
    const reference = params.get('subscription');
    if (!reference) return;
    window.history.replaceState({}, '', window.location.pathname);
    if (reference === 'cancelled') {
      notify('Checkout was cancelled. Nothing was charged and no trial started.');
      return;
    }
    void (async () => {
      try {
        const result = await api.post(
          `/organizations/${orgId}/billing/subscribe/confirm`, { reference });
        if (result.status === 'checkout_incomplete') {
          notify('Checkout was not completed, so no trial has started.');
        } else {
          notify('Your sending trial is active.', 'ok');
        }
      } catch {
        // The webhook is the authority; a failure here is not the customer's
        // problem and must not be reported as one.
      } finally {
        await load();
      }
    })();
  }, [orgId, notify, load]);

  const buyCredits = async (count: number, first: boolean) => {
    const terms = data.credit_offer;
    setBusy(true); setError(null);
    try {
      await guard(async () => {
        const path = first ? 'activate' : 'purchase';
        const body = first
          ? { packs: count, accepted_terms: true,
              disclosure_version: terms.disclosure_version,
              consent_version: terms.consent_version }
          : { packs: count };
        const result = await api.post(
          `/organizations/${orgId}/credits/${path}`, body);
        const purchase = result.purchase ?? result;
        if (purchase?.outcome === 'failed') {
          // A decline is reported, never swallowed: no credits were added.
          setError(purchase.message || 'The card was declined. No credits were added.');
        } else {
          notify(`${purchase.credits_added} credits added.`, 'ok');
        }
        await load();
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : 'Those credits could not be bought.');
    } finally { setBusy(false); }
  };

  const setAutoRecharge = async (enabled: boolean) => {
    setBusy(true); setError(null);
    try {
      await guard(async () => {
        await api.post(`/organizations/${orgId}/credits/auto-recharge`, { enabled });
        notify(enabled ? 'Automatic top-up is on.' : 'Automatic top-up is off.', 'ok');
        await load();
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : 'That setting could not be saved.');
    } finally { setBusy(false); }
  };

  // Subscribing leaves this page: the card is entered on the provider's own
  // hosted checkout and the trial starts when the provider creates the
  // subscription there. Nothing here can report an activated trial, so
  // nothing here claims one.
  const subscribe = async () => {
    const disclosure = pricing.plans.find((p: any) => p.plan_id === plan);
    setBusy(true); setError(null);
    try {
      await guard(async () => {
        const result = await api.post(`/organizations/${orgId}/billing/subscribe`, {
          plan_id: plan, accepted_terms: true,
          disclosure_version: disclosure.disclosure_version,
          consent_version: disclosure.consent_version,
        });
        window.location.assign(result.checkout.url);
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : 'Checkout could not be opened.');
      setBusy(false);
    }
  };

  // Where a card is changed, a failed payment retried and an invoice found.
  const openPortal = async () => {
    setBusy(true); setError(null);
    try {
      await guard(async () => {
        const result = await api.post(`/organizations/${orgId}/billing/portal`, {});
        window.location.assign(result.portal.url);
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                        : 'The billing portal could not be opened.');
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await guard(async () => {
        const result = await api.post(`/organizations/${orgId}/billing/cancel`,
                                      { reason: cancelReason });
        notify(result.summary || 'Cancellation recorded.', 'ok');
        setCancelOpen(false);
        await load();
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Cancellation failed.');
    } finally { setBusy(false); }
  };

  if (!data || !pricing) {
    return <AppFrame><div className="shell section"><Loading /></div></AppFrame>;
  }

  const ent = data.entitlements;
  const allowance = ent.allowance;
  const selected = pricing.plans.find((p: any) => p.plan_id === plan);
  // Two different requirements, deliberately not one flag. Subscribing needs
  // no card here -- the provider collects it during checkout and requires it
  // before the trial starts. Buying credits is a charge made now, against a
  // card already on file, so it does need one.
  const canSubscribe = ent.status === 'none';
  const canBuyCredits = Boolean(data.payment_method) && ent.status === 'none';

  return (
    <AppFrame>
      <div className="shell section stack-l">
        <header>
          <h1 style={{ margin: 0 }}>Billing</h1>
          <p className="muted small" style={{ margin: '4px 0 0' }}>
            All times shown in {timeZoneName()}.
          </p>
        </header>

        {error && <Banner tone="danger" title="That did not work">{error}</Banner>}

        {ent.tier === 'credits' ? (
          <CreditsPanel
            credits={ent.credits}
            offer={data.credit_offer}
            busy={busy}
            packs={packs}
            setPacks={setPacks}
            onBuy={(count: number) => void buyCredits(count, false)}
            onAutoRecharge={(enabled: boolean) => void setAutoRecharge(enabled)}
          />
        ) : (
        <div className="card stack">
          <div className="row row--between">
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Subscription</h2>
            <span className={`pill pill--${
              ent.status === 'active' ? 'ok' : ent.status === 'trialing' ? 'brand'
              : ent.status === 'past_due' ? 'danger' : ''}`}>
              {ent.status === 'none' ? 'Not activated' : ent.status.replace('_', ' ')}
            </span>
          </div>
          <div className="kv">
            <div className="kv__k">Plan</div>
            <div className="kv__v">{ent.plan_name} — {ent.price_display} per year</div>
            <div className="kv__k">Monthly allowance</div>
            <div className="kv__v">{ent.monthly_requests} signature requests</div>
            <div className="kv__k">API access</div>
            <div className="kv__v">{ent.api_access ? 'Included' : 'Pro plan only'}</div>
            {ent.trial_ends_at && (
              <>
                <div className="kv__k">Trial ends</div>
                <div className="kv__v">{formatExact(ent.trial_ends_at)}</div>
              </>
            )}
            {ent.first_charge_at && (
              <>
                <div className="kv__k">First charge</div>
                <div className="kv__v"><strong>{formatExact(ent.first_charge_at)}</strong></div>
              </>
            )}
            {ent.current_term_end && (
              <>
                <div className="kv__k">Current term ends</div>
                <div className="kv__v">{formatDate(ent.current_term_end)}</div>
              </>
            )}
            {ent.canceled_at && (
              <>
                <div className="kv__k">Canceled</div>
                <div className="kv__v">{formatDateTime(ent.canceled_at)}</div>
              </>
            )}
          </div>

          {ent.status === 'trialing' && (
            <Banner tone="warn" title="Billing starts when the trial expires">
              {ent.price_display} will be charged on{' '}
              <strong>{formatExact(ent.first_charge_at)}</strong> unless you cancel
              first — even if your recipients have not finished signing. The
              subscription then renews annually until canceled.
            </Banner>
          )}

          {ent.cancel_at_term_end && (
            <Banner tone="info" title="Renewal is off">
              Your paid access continues until {formatDate(ent.current_term_end)}.
              Completed agreements and evidence remain retained and retrievable
              afterwards.
            </Banner>
          )}

          <div className="btn-row btn-row--stack">
            {ent.portal_available && (
              <button className="btn" disabled={busy} onClick={() => void openPortal()}>
                {busy ? 'Opening…' : 'Manage payment & invoices'}
              </button>
            )}
            {(ent.status === 'trialing' || ent.status === 'active' || ent.status === 'past_due')
              && !ent.cancel_at_term_end && (
              <button className="btn btn--danger" onClick={() => setCancelOpen(true)}>
                {ent.status === 'trialing' ? 'Cancel trial' : 'Turn off renewal'}
              </button>
            )}
          </div>
          {ent.portal_available && (
            <p className="small muted" style={{ margin: 0 }}>
              Card changes, receipts and past invoices are handled on the payment
              provider's own pages.
            </p>
          )}
        </div>
        )}

        {allowance.state !== 'inactive' && allowance.state !== 'credits' && (
          <div className="card stack-s">
            <div className="row row--between">
              <h2 style={{ margin: 0, fontSize: '1.1rem' }}>
                {allowance.state === 'trial' ? 'Trial allowance' : 'Current period'}
              </h2>
              <span className="small muted">
                {allowance.used} of {allowance.allowance}
              </span>
            </div>
            <Meter used={allowance.used} allowance={allowance.allowance} />
            <p className="small muted" style={{ margin: 0 }}>
              Resets {formatExact(allowance.next_reset_at)}. Unused requests do not
              roll over. Reaching the limit blocks new sends only — drafts, existing
              signing links, recipient completion and archive access are unaffected.
            </p>
          </div>
        )}

        {ent.status === 'none' && (
          <div className="card stack">
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Activate sending</h2>

            {!data.payment_method ? (
              <div className="stack">
                <p className="muted small" style={{ margin: 0 }}>
                  A card is required to activate the trial. It is entered on the
                  payment provider’s own page — eSignLoop receives a reference and
                  the last four digits, never the card number or security code.
                </p>
                {!collecting ? (
                  <button className="btn btn--block" disabled={busy}
                          onClick={() => void beginCardSetup()}>
                    {busy ? 'Starting…' : 'Add a card'}
                  </button>
                ) : (
                  <div className="stack-s">
                    {setup?.simulated && (
                      <Banner tone="info" title="Simulated payment provider">
                        This environment uses a payment simulator. No card is
                        charged and no real card should be entered.
                      </Banner>
                    )}
                    {/* The card form is the provider's own document, served
                        from the provider's origin. This application cannot read
                        into it, and the only thing that comes back out is the
                        reference, by postMessage from that one origin. */}
                    <iframe src={setup.url} title="Payment details"
                            className="provider-frame"
                            style={frameHeight ? { height: frameHeight } : undefined} />
                    <button className="btn btn--ghost btn--small"
                            onClick={() => { setCollecting(false); setSetup(null); }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Banner tone="ok" title="Card saved">
                {data.payment_method.brand} ending {data.payment_method.last4}, expires{' '}
                {String(data.payment_method.exp_month).padStart(2, '0')}/
                {data.payment_method.exp_year}. Saving a card does not start your
                trial — activation below is a separate step.
              </Banner>
            )}

            <hr className="divider" />

            {/* Two tiers, and they are genuinely different shapes: one has a
                term and a monthly allowance, the other has a balance and no
                deadline. Presenting them as one list of "plans" would hide
                that. */}
            <div className="row row--tight" role="tablist" aria-label="Choose how to pay">
              {([
                ['subscription', 'Annual plan'],
                ['credits', 'Pay as you go'],
              ] as const).map(([key, label]) => (
                <button key={key} type="button" role="tab"
                        aria-selected={tier === key}
                        className={`btn btn--small${tier === key ? ' btn--primary' : ' btn--ghost'}`}
                        onClick={() => { setTier(key); setAccepted(false); }}>
                  {label}
                </button>
              ))}
            </div>

            {tier === 'credits' ? (
              <div className="stack">
                <div className="panel stack-s">
                  <strong style={{ fontSize: '.92rem' }}>Before you buy</strong>
                  <div className="kv">
                    <div className="kv__k">Price</div>
                    <div className="kv__v">
                      {data.credit_offer.price_display} for{' '}
                      {data.credit_offer.credits} credits ({data.credit_offer.currency})
                    </div>
                    <div className="kv__k">Cost per send</div>
                    <div className="kv__v">
                      {data.credit_offer.credits_per_send} credits —{' '}
                      {data.credit_offer.sends_per_pack} sends per pack
                    </div>
                    <div className="kv__k">Expiry</div>
                    <div className="kv__v"><strong>{data.credit_offer.expiry}</strong></div>
                    <div className="kv__k">Renewal</div>
                    <div className="kv__v">{data.credit_offer.renewal_cadence}</div>
                    <div className="kv__k">Automatic top-up</div>
                    <div className="kv__v">{data.credit_offer.auto_recharge}</div>
                    <div className="kv__k">API access</div>
                    <div className="kv__v">{data.credit_offer.api_access}</div>
                    <div className="kv__k">Sealing</div>
                    <div className="kv__v">{data.credit_offer.sealing}</div>
                    <div className="kv__k">Tax</div>
                    <div className="kv__v">{data.credit_offer.tax_treatment}</div>
                    <div className="kv__k">Cancellation</div>
                    <div className="kv__v">{data.credit_offer.cancellation}</div>
                  </div>
                </div>

                <Field label="Packs" id="first-packs"
                       hint={`${packs * data.credit_offer.credits} credits · ${packs * data.credit_offer.sends_per_pack} sends`}>
                  <input id="first-packs" className="input" type="number" min={1}
                         max={20} value={packs} disabled={!canBuyCredits}
                         onChange={(event) => setPacks(
                           Math.min(20, Math.max(1, Number(event.target.value) || 1)))} />
                </Field>

                <label className="checkbox">
                  <input type="checkbox" checked={accepted} disabled={!canBuyCredits}
                         onChange={(event) => setAccepted(event.target.checked)} />
                  <span className="checkbox__text">
                    I have read the terms above and agree to buy{' '}
                    {packs * data.credit_offer.credits} credits for{' '}
                    ${((data.credit_offer.price_cents * packs) / 100).toFixed(2)},
                    charged now. Nothing renews.
                  </span>
                </label>

                <button className="btn btn--primary btn--large btn--block"
                        disabled={busy || !canBuyCredits || !accepted}
                        onClick={() => void buyCredits(packs, true)}>
                  {busy ? 'Buying…'
                        : `Buy ${packs * data.credit_offer.credits} credits`}
                </button>
              </div>
            ) : (
            <div className="stack">
            <Field label="Choose a plan" id="plan">
              <select id="plan" className="select" value={plan}
                      onChange={(event) => setPlan(event.target.value)}>
                {pricing.plans.map((item: any) => (
                  <option key={item.plan_id} value={item.plan_id}>
                    {item.plan_name} — {item.price_display}/year, {item.monthly_requests} requests/month
                    {item.api_access ? ', API access' : ''}
                  </option>
                ))}
              </select>
            </Field>

            {selected && (
              <div className="panel stack-s">
                <strong style={{ fontSize: '.92rem' }}>Before you activate</strong>
                <div className="kv">
                  <div className="kv__k">Price</div>
                  <div className="kv__v">{selected.price_display} per year ({selected.currency}, billed annually)</div>
                  <div className="kv__k">Trial</div>
                  <div className="kv__v">
                    {selected.trial_days} days, {selected.trial_requests} signature requests
                  </div>
                  <div className="kv__k">First charge</div>
                  <div className="kv__v">
                    <strong>{formatExact(selected.expected_first_charge_at)}</strong>
                  </div>
                  <div className="kv__k">Renewal</div>
                  <div className="kv__v">{selected.renewal_cadence}</div>
                  <div className="kv__k">Cancellation</div>
                  <div className="kv__v">{selected.cancellation}</div>
                  <div className="kv__k">Tax</div>
                  <div className="kv__v">{selected.tax_treatment}</div>
                </div>
                <p className="small" style={{ margin: 0, color: 'var(--warn)' }}>
                  {selected.billing_starts_note}
                </p>
              </div>
            )}

            <label className="checkbox">
              <input type="checkbox" checked={accepted} disabled={!canSubscribe}
                     onChange={(event) => setAccepted(event.target.checked)} />
              <span className="checkbox__text">
                I have read the terms above and agree to start the {selected?.trial_days}-day
                sending trial, with {selected?.price_display} charged at trial expiry
                unless I cancel first.
              </span>
            </label>

            <button className="btn btn--primary btn--large btn--block"
                    disabled={busy || !canSubscribe || !accepted}
                    onClick={() => void subscribe()}>
              {busy ? 'Opening checkout…' : 'Continue to secure checkout'}
            </button>
            <p className="small muted" style={{ margin: 0 }}>
              Your card is entered on the payment provider's own page. The trial
              starts, and the exact first-charge time is confirmed, once they
              have it.
            </p>
            </div>
            )}
          </div>
        )}

        {data.usage_history.length > 0 && (
          <div className="card card--pad-0">
            <div style={{ padding: '16px 18px 0' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Usage history</h2>
            </div>
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="table">
                <thead>
                  <tr><th>Period</th><th>From</th><th>To</th><th>Used</th><th>Allowance</th></tr>
                </thead>
                <tbody>
                  {data.usage_history.map((period: any, index: number) => (
                    <tr key={index}>
                      <td>{period.kind === 'trial' ? 'Trial' : `Month ${period.period_index + 1}`}</td>
                      <td className="nowrap">{formatDate(period.starts_at)}</td>
                      <td className="nowrap">{formatDate(period.ends_at)}</td>
                      <td>{period.used}</td>
                      <td>{period.allowance}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>

      {challenge}
      {cancelOpen && (
        <Modal title={ent.status === 'trialing' ? 'Cancel your trial' : 'Turn off renewal'}
               onClose={() => setCancelOpen(false)}
               actions={<>
                 <button className="btn" onClick={() => setCancelOpen(false)}>Keep it</button>
                 <button className="btn btn--danger" disabled={busy}
                         onClick={() => void cancel()}>Confirm</button>
               </>}>
          <p className="small muted">
            {ent.status === 'trialing'
              ? 'Canceling now prevents the first charge entirely.'
              : `Renewal stops and your paid access continues until ${formatDate(ent.current_term_end)}.`}
            {' '}Either way you keep access to retrieve and export completed
            agreements.
          </p>
          <Field label="Reason (optional)" id="cancel-reason">
            <textarea id="cancel-reason" className="textarea" value={cancelReason}
                      onChange={(event) => setCancelReason(event.target.value)} />
          </Field>
        </Modal>
      )}
    </AppFrame>
  );
}

/** The prepaid tier: a balance, a top-up, and an honest statement that
 *  nothing here expires or renews. Deliberately not a meter — a meter implies
 *  a period, and credits have none. */
function CreditsPanel({ credits, offer, busy, packs, setPacks, onBuy,
                        onAutoRecharge }: {
  credits: any; offer: any; busy: boolean; packs: number;
  setPacks: (n: number) => void;
  onBuy: (packs: number) => void;
  onAutoRecharge: (enabled: boolean) => void;
}) {
  const total = (offer.price_cents * packs) / 100;
  return (
    <div className="card stack">
      <div className="row row--between">
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Credits</h2>
        <span className={`pill pill--${credits.sending_enabled ? 'ok' : 'danger'}`}>
          {credits.balance} credits
        </span>
      </div>

      <div className="kv">
        <div className="kv__k">Balance</div>
        <div className="kv__v">
          <strong>{credits.balance} credits</strong> — {credits.sends_remaining}{' '}
          {credits.sends_remaining === 1 ? 'send' : 'sends'}
        </div>
        <div className="kv__k">Cost per send</div>
        <div className="kv__v">{credits.credits_per_send} credits</div>
        <div className="kv__k">Expiry</div>
        <div className="kv__v">Credits do not expire.</div>
        <div className="kv__k">API access</div>
        <div className="kv__v">Pro plan only</div>
      </div>

      {!credits.sending_enabled && (
        <Banner tone="danger" title="No credits left">
          {credits.reason} Agreements already out for signature are unaffected,
          and recipients can still complete them.
        </Banner>
      )}

      {credits.auto_recharge_paused && (
        <Banner tone="danger" title="Automatic top-up is paused">
          The last top-up was declined{credits.auto_recharge_pause_reason
            ? ` (${credits.auto_recharge_pause_reason})` : ''}. Your existing
          credits are unaffected. Update your card, then turn it back on.
        </Banner>
      )}

      <hr className="divider" />

      <div className="stack-s">
        <strong style={{ fontSize: '.92rem' }}>Buy credits</strong>
        <p className="small muted" style={{ margin: 0 }}>
          {offer.price_display} for {offer.credits} credits —{' '}
          {offer.sends_per_pack} sends. Credits do not expire.
        </p>
        <div className="row row--tight">
          <label className="small muted" htmlFor="credit-packs">Packs</label>
          <input id="credit-packs" className="input" type="number" min={1} max={20}
                 style={{ maxWidth: 90 }} value={packs}
                 onChange={(event) => setPacks(
                   Math.min(20, Math.max(1, Number(event.target.value) || 1)))} />
          <button className="btn" disabled={busy} onClick={() => onBuy(packs)}>
            {busy ? 'Working…'
                  : `Buy ${packs * offer.credits} credits — $${total.toFixed(2)}`}
          </button>
        </div>
      </div>

      <hr className="divider" />

      <label className="checkbox">
        <input type="checkbox" checked={credits.auto_recharge && !credits.auto_recharge_paused}
               disabled={busy}
               onChange={(event) => onAutoRecharge(event.target.checked)} />
        <span>
          Top up automatically. When the balance falls below{' '}
          {credits.auto_recharge_threshold} credits — less than one send — buy
          one more {offer.price_display} pack. Turn it off at any time.
        </span>
      </label>
    </div>
  );
}
